import { toast } from 'react-toastify'
import swal from 'sweetalert2'

import { faUpload } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Box, Button, Form, FormField, Link, SidebarModal,
} from '@domain-group/suit-theme'

import type { SidebarModalProps } from '@domain-group/suit-theme'

import useLoginToRealhub from '@hooks/useLoginToRealhub'
import useTemplatePage, { useTemplatePageForm } from '@hooks/useTemplatePage'

import {
  faAngleRight,
  faArchive,
  faDownload,
  faThumbsDown,
  faThumbsUp,
} from '@fortawesome/pro-light-svg-icons'

import type { TemplatePageModel } from '@models/types'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import AuthenticatedDownload from '@components/AuthenticatedDownload'
import PreviewImage from './components/PreviewImage'
import TagInput from './components/TagInput'
import { useState } from 'react'

const MODAL_KEY = 'EditTemplateModal'

type EditTemplateModalProps = {
  callbacks: {
    editTemplatePage: any,
  },
  templatePage: TemplatePageModel,
  showModal: boolean,
}

const confirmToggleProductionReady = (productionReady: boolean | undefined, toggleProductionReadyFn: () => void) => {
  const toggleProductionReadyPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: toggleProductionReadyFn,
    },
    toastText: productionReady ? 'Template Unapproved' : 'Template Approved',
  }

  swal
    .fire({
      title: productionReady ? 'Unapprove Template?' : 'Approve Template?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${productionReady ? 'unapprove' : 'approve'} it.`,
      confirmButtonColor: productionReady ? '#DD6B55' : '#43ac6a',
    })
    .then(({ value }) => {
      if (value){
        handleCallbackAction(toggleProductionReadyPayload)
      }
    })
}

const EditTemplateModal = (props: SidebarModalProps & EditTemplateModalProps) => {
  const {
    templatePage,
    showModal,
    callbacks,
    callbacks: { editTemplatePage, closeModal },
  } = props

  const templatePagePayload = useTemplatePage(templatePage)

  const { templatePage: currentTemplatePage } = templatePagePayload
  const {
    callbacks: {
      assignTemplatePagePermissions,
      deleteTemplatePage,
      manageTemplatePageActivity,
      toggleProductionReady,
      updateTemplatePage: updateFn,
      uploadTemplatePagePreview,
      viewArtworkInUse,
    },
    urls: {
      templatePageDownloadPdfUrl,
    },
    templatePage: {
      production_ready,
      in_use_count: inUseCount,
      id: templatePageId,
    },
  } = templatePagePayload

  const formPayload = useTemplatePageForm(templatePage, {
    validateOn: 'change',
  })

  const {
    entityState,
    entityState: { title },
    handlers,
    saveEnabled,
  } = formPayload

  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(templatePage.tags ?? [])

  const handleSave = async () => {
    const mergedEntityParams = {
      ...entityState,
      tags: selectedTagIds,
    }

    editTemplatePage({
      callbacks: {
        action: updateFn,
        afterAction: closeModal,
      },
      entityParams: mergedEntityParams,
      toastText: 'Template Page Updated',
    })
  }

  const handleUploadPreview = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      const result = await uploadTemplatePagePreview(file)
      if (result?.success) toast.success('Preview image updated')
    }
    input.click()
  }


  const confirmDelete = (deleteFn: () => void) => {
    const deleteTemplatePagePayload: HandleCallbackActionParams = {
      callbacks: {
        // @ts-expect-error TS2322
        action: deleteFn,
        afterAction: closeModal,
      },
      toastText: 'Template Archived',
    }

    swal
      .fire({
        title: 'Archive Template?',
        text: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, archive it.',
        confirmButtonColor: '#DD6B55',
      })
      .then(({ value }) => {
        if (value){
          handleCallbackAction(deleteTemplatePagePayload)
        }
      })
  }

  const loginToRealhubPayload = useLoginToRealhub({
    redirect: { key: 'editTemplatePage', params: { templatePageId: templatePageId } },
  })

  const { callbacks: { loginToRealhub } } = loginToRealhubPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Template" />

      <SidebarModal.Content>
        <Box flexDirection="column">
          <Box
            alignItems="center"
            backgroundColor="white"
            borderRadius={5}
            border="1px solid"
            borderColor="lineColor"
            justifyContent="center"
            marginTop="medium"
          >
            <Box
              height={300}
              borderTopLeftRadius={5}
              borderBottomLeftRadius={5}
              borderRight="1px solid"
              borderColor="lineColor"
              backgroundColor="whiteGrey"
              alignItems="center"
              justifyContent="center"
              flexShrink="0"
              width={200}
            >
              <PreviewImage
                height={250}
                templatePage={currentTemplatePage}
                width={200}
                boxProps={{ marginX: 'auto' }}
              />
            </Box>
          </Box>

          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faUpload} />}
            onClick={handleUploadPreview}
            size="small"
            // @ts-expect-error TS2322
            width="auto"
            marginTop="small"
          >
            Change Preview
          </Button>

          <TagInput selectedTagIds={selectedTagIds} setSelectedTagIds={setSelectedTagIds} />

          <FormField direction="column" label="* Template Title" marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </FormField>

          <Form.Field label="Manage" marginTop="large">
            <SidebarModal.Button
              boxProps={{ paddingY: ['xlarge', 'large'] }}
              iconRight={<FontAwesomeIcon icon={faAngleRight} />}
              onClick={() => loginToRealhub()}
              title="Edit Template"
            />

            <SidebarModal.Button
              boxProps={{ paddingY: ['xlarge', 'large'] }}
              iconRight={<FontAwesomeIcon icon={faAngleRight} />}
              onClick={manageTemplatePageActivity}
              title="View Comments & Activity"
            />

          {!!inUseCount && (
              <SidebarModal.Button
                boxProps={{ paddingY: ['xlarge', 'large'] }}
                iconRight={<FontAwesomeIcon icon={faAngleRight} />}
                onClick={viewArtworkInUse}
                title="Artwork in use"
                subtitle={`Artwork in use: ${inUseCount}`}
              />
            )}

            <SidebarModal.Button
              boxProps={{ paddingY: ['xlarge', 'large'] }}
              iconRight={<FontAwesomeIcon icon={faAngleRight} />}
              onClick={() => assignTemplatePagePermissions('providerIds')}
              title="Manage Permissions"
            />
          </Form.Field>

          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faArchive} />}
              onClick={() => confirmDelete(deleteTemplatePage)}
              // @ts-expect-error TS2322
              marginBottom="small"
              size="medium"
            >
              Archive
            </Button>

            <AuthenticatedDownload key="download-template">
              <Link href={templatePageDownloadPdfUrl}>
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  // @ts-expect-error TS2322
                  marginBottom="small"
                  size="medium"
                >
                  Download Template
                </Button>
              </Link>
            </AuthenticatedDownload>

            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={production_ready ? faThumbsDown : faThumbsUp} />}
              onClick={() => confirmToggleProductionReady(production_ready, toggleProductionReady)}
              // @ts-expect-error TS2322
              marginBottom="small"
              size="medium"
            >
              {production_ready ? 'Unapprove' : 'Approve for Production'}
            </Button>
          </Form.Field>
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loadingBubbleColor="white"
          onClick={handleSave}
          size="large"
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: SidebarModalProps & EditTemplateModalProps) => (
  // @ts-expect-error TS2786
  <SidebarModal.RenderController {...props}>
    <EditTemplateModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
